class LandingProductDoubleSection extends HTMLElement {
  constructor() {
    super();

    this.stepBlocks = this.querySelectorAll('[data-step]');
    this.stepInputs = this.querySelectorAll('.landing-product-double__choice');
    this.finalBlock = this.querySelector('.final-step__list');
    this.productsElements = this.querySelectorAll('[data-step-name="product"]');
    this.addButton = this.querySelector('.final-step__product__button');
    this.clickedBlock = null;
    this.isFinalBlock = false;
    this.layout = this.getAttribute('data-layout');
    this.purchaseType = this.dataset.purchaseType;
    this.isOpenedByDefault = this.hasAttribute('data-is-opened-by-default');
    this.isInitialized = false;
    this.isKeyboardStepConfirmation = false;
    this.atcErrorTimeout = null;
    this.atcErrorBanner = this.createAtcErrorBanner();
    this.handlePageShow = () => {
      this.resetAtcState();
    };
    this.handleSectionInteraction = event => {
      if (
        event.target instanceof Element &&
        event.target.closest(
          '.final-step__product__button, [data-step], [data-progress-step-number], button'
        )
      ) {
        this.hideAtcError();
      }
    };

    this.stepBlocks.forEach(block => {
      block.addEventListener('click', event => {
        if (
          event.target.classList.contains('landing-product-double__choice') &&
          event.detail === 0
        ) {
          return;
        }

        this.clickedBlock = block;
        this.highlightBlock();
      });
    });

    this.stepInputs.forEach(input => {
      input.addEventListener('change', () => {
        this.selectStepBlock(input.closest('[data-step]'));
      });

      input.addEventListener('keydown', event => {
        if (event.key !== 'Enter' || event.repeat) return;

        event.preventDefault();
        this.selectStepBlock(input.closest('[data-step]'));
        this.isKeyboardStepConfirmation = true;
        try {
          this.highlightBlock();
        } finally {
          this.isKeyboardStepConfirmation = false;
        }
        requestAnimationFrame(() => this.focusCurrentKeyboardTarget());
      });
    });

    this.addButton?.addEventListener(
      'click',
      this.clearCartAndAddToCart.bind(this)
    );
    this.handleSectionAnchor = event => {
      if (!this.shouldScrollToSectionStart() || event.defaultPrevented || event.button !== 0
        || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[href]');
      if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname
        || url.search !== window.location.search || !this.isSectionAnchor(url.hash)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (window.location.hash !== url.hash) window.history.pushState(null, '', url.hash);
      this.scrollToStart();
    };
    this.handleInitialSectionAnchor = () => {
      if (this.shouldScrollToSectionStart() && this.isSectionAnchor(window.location.hash)) {
        requestAnimationFrame(() => {
          if (this.isConnected) this.scrollToStart();
        });
      }
    };
  }

  connectedCallback() {
    this.resetAtcState();
    window.addEventListener('pageshow', this.handlePageShow);

    this.addEventListener(
      'click',
      this.handleSectionInteraction,
      true
    );

    if (this.shouldScrollToSectionStart()) {
      document.addEventListener('click', this.handleSectionAnchor, true);
      window.addEventListener('hashchange', this.handleInitialSectionAnchor);
      if (document.readyState === 'complete') {
        this.handleInitialSectionAnchor();
      } else {
        window.addEventListener('load', this.handleInitialSectionAnchor, { once: true });
      }
    }

    if (this.isOpenedByDefault) {
      const currentActiveProduct = this.querySelector(
        '[data-step-name="product"].step-block__item--active'
      );

      if (currentActiveProduct) {
        this.clickedBlock = currentActiveProduct;
        this.highlightBlock();
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleSectionAnchor, true);
    window.removeEventListener('load', this.handleInitialSectionAnchor);
    window.removeEventListener('hashchange', this.handleInitialSectionAnchor);
    this.hideAtcError();
    window.removeEventListener('pageshow', this.handlePageShow);
    this.removeEventListener(
      'click',
      this.handleSectionInteraction,
      true
    );
  }

  shouldScrollToSectionStart() {
    return this.hasAttribute('data-subscription-landing')
      || (this.localName === 'landing-product-refined' && this.dataset.productStepScroll === 'heading');
  }

  isSectionAnchor(hash) {
    return hash === `#${this.id}` || (hash === '#section-page-landing-50-new-scroll'
      && this.contains(document.getElementById('section-page-landing-50-new-scroll')));
  }

  getStickyHeaderHeight() {
    return Array.from(document.querySelectorAll(
      'landing-sticky-header, sticky-header, .shopify-section-header-sticky, .landing-header-v2, .landing-header-v3'
    )).reduce((height, header) => {
      const rect = header.getBoundingClientRect();
      const position = getComputedStyle(header).position;
      return (position === 'fixed' || position === 'sticky') && rect.top <= 0 && rect.bottom > 0
        ? Math.max(height, rect.bottom)
        : height;
    }, 0);
  }

  scrollToStart(target = this.querySelector('.landing-product__header') || this) {
    window.scrollTo({
      top: Math.max(0, target.getBoundingClientRect().top + window.scrollY - this.getStickyHeaderHeight() - 16),
      behavior: 'smooth',
    });
  }

  resetAtcState() {
    if (this.addButton) {
      this.addButton.disabled = false;
    }

    this.hideAtcError();
  }

  selectStepBlock(block) {
    if (!block) return;

    const currentStep = block.dataset.step;
    this.querySelector(
      `[data-step="${currentStep}"].step-block__item--active`
    )?.classList.remove('step-block__item--active');

    const input = block.querySelector('.landing-product-double__choice');
    if (input) input.checked = true;

    block.classList.add('step-block__item--active');
    this.clickedBlock = block;
  }

  focusCurrentKeyboardTarget() {
    if (this.dataset.currentStep === 'final' || this.finalBlock?.classList.contains('active')) {
      this.addButton?.focus();
      return;
    }

    const currentStep = this.dataset.currentStep;
    const currentChoice = this.querySelector(
      `[data-step="${currentStep}"]:not(.step-block__item--hidden) > .landing-product-double__choice`
    );
    const currentStepWrapper = currentChoice?.closest('.landing-product__step');

    if (
      currentChoice &&
      currentStepWrapper?.classList.contains('step-block--active')
    ) {
      currentChoice.focus();
      return;
    }

    const activeSteps = Array.from(
      this.querySelectorAll('.landing-product__step.step-block--active')
    ).reverse();
    const nextChoice = activeSteps
      .map(step => step.querySelector(
        '.step-block__item:not(.step-block__item--hidden) > .landing-product-double__choice'
      ))
      .find(Boolean);

    nextChoice?.focus();
  }

  createAtcErrorBanner() {
    if (!this.addButton) return null;

    const banner = document.createElement('div');

    banner.className = 'landing-product__atc-error';
    banner.setAttribute('role', 'alert');
    banner.setAttribute('aria-live', 'assertive');
    banner.hidden = true;

    this.addButton.insertAdjacentElement('afterend', banner);

    return banner;
  }

  showAtcError() {
    if (!this.atcErrorBanner) return;

    window.clearTimeout(this.atcErrorTimeout);

    this.atcErrorBanner.textContent =
      window.cartStrings?.error ||
      'Something went wrong while adding your product to the cart. Please try again.';
    this.atcErrorBanner.hidden = false;

    this.atcErrorTimeout = window.setTimeout(() => {
      this.hideAtcError();
    }, 5000);
  }

  hideAtcError() {
    window.clearTimeout(this.atcErrorTimeout);
    this.atcErrorTimeout = null;

    if (this.atcErrorBanner) {
      this.atcErrorBanner.hidden = true;
    }
  }

  async getCart() {
    const response = await fetch(
      `${window.Shopify.routes.root}cart.js`,
      {
        cache: 'no-store',
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to get cart: ${response.status}`);
    }

    return response.json();
  }

  async clearCart() {
    const response = await fetch(
      `${window.Shopify.routes.root}cart/clear.js`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to clear cart: ${response.status}`);
    }

    return response.json();
  }

  async clearCartAndAddToCart(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.addButton?.disabled) return;

    try {
      if (this.addButton) {
        this.addButton.disabled = true;
      }

      const cart = await this.getCart();

      if (cart.item_count > 0) {
        await this.clearCart();
      }

      await this.addToCart();
    } catch (error) {
      console.error('Checkout flow error:', error);
      this.showAtcError();

      if (this.addButton) {
        this.addButton.disabled = false;
      }
    }
  }

  highlightBlock() {
    let currentStep = this.clickedBlock.dataset.step;
    let currentStepName = this.clickedBlock.dataset.stepName;
    let prevActiveBlock = this.querySelector(
      `[data-step="${currentStep}"].step-block__item--active`
    );

    if (currentStepName === 'type' && !this.isOpenedByDefault) {
      this.querySelectorAll('.step-block__item--active').forEach(activeElement => {
        activeElement.classList.remove('step-block__item--active');

        this.querySelector('[data-step-name="product"]')
          ?.closest('.step-block')
          ?.classList.remove('step-block--active');
      });

      this.isFinalBlock = false;
    }

    if (currentStepName === 'roast') {
      this.isFinalBlock = false;
    }

    if (prevActiveBlock) {
      prevActiveBlock.classList.remove('step-block__item--active');
    }

    this.clickedBlock.classList.add('step-block__item--active');

    if (currentStepName === 'product') {
      let productItem = this.querySelector(
        '[data-step-name="product"].step-block__item--active'
      );

      let prevProduct = this.querySelector(
        '.final-step__product--active'
      );

      if (prevProduct) {
        prevProduct.classList.remove('final-step__product--active');
      }

      productItem.classList.add('final-step__product--active');

      this.setButtonPrice(productItem);
    }

    this.openNextStep();
  }

  openNextStep() {
    let nextStep = +this.clickedBlock.dataset.step + 1;
    let nextStepWrapper = this.querySelector(`[data-step="${nextStep}"]`);
    let nextStepName = nextStepWrapper?.dataset?.stepName;

    this.setAttribute('data-current-step', +nextStep);

    this.previousStepButton?.classList.toggle(
      'active',
      nextStep !== 1
    );

    this.isFinalBlock
      ? this.showFinalBlock()
      : this.hideFinalBlock();

    if (nextStepWrapper && nextStepName === 'roast') {
      this.openRoastStep(nextStepWrapper);
    } else if (nextStepWrapper && nextStepName === 'product') {
      this.openProductStep(nextStepWrapper);
    } else {
      this.showFinalBlock();
    }
  }

  openRoastStep(nextStepWrapper) {
    if (this.clickedBlock.dataset.variant === 'Default Title') {
      nextStepWrapper
        .closest('.step-block')
        .classList.add('step-block--default-variant-active');

      nextStepWrapper
        .closest('.step-block')
        .classList.remove('step-block--active');
    } else {
      nextStepWrapper
        .closest('.step-block')
        .classList.add('step-block--active');

      nextStepWrapper
        .closest('.step-block')
        .classList.remove('step-block--default-variant-active');
    }

    if (!this.isFinalBlock && this.layout !== 'quiz') {
      const activeNextBlock =
        this.clickedBlock.closest('.step-block')?.nextElementSibling;

      if (!activeNextBlock) return;

      window.scrollTo({
        top: activeNextBlock.offsetTop - 150,
        behavior: 'smooth',
      });
    }
  }

  openProductStep(nextStepWrapper) {
    let activeRoastId = this.querySelector(
      '[data-step-name="roast"].step-block__item--active'
    )?.dataset.product;

    this.productsElements.forEach(productElement => {
      if (
        productElement.dataset.finalProductId === activeRoastId ||
        this.productsElements.length === 2
      ) {
        productElement.classList.remove(
          'step-block__item--hidden'
        );

        if (
          productElement.dataset.quantity === '6' ||
          productElement.dataset.quantity === '4'
        ) {
          if (!this.isFinalBlock && !this.isKeyboardStepConfirmation) {
            productElement.click();
          }
        }

        const variantTitleElement = productElement.querySelector(
          '.final-step__product__variant'
        );

        if (variantTitleElement) {
          const variantBlock = this.querySelector(
            '.step-block__item--active[data-variant]'
          );

          const selectedVariant =
            variantBlock?.dataset.variant !== 'Default Title'
              ? variantBlock.dataset.variant
              : '';

          variantTitleElement.innerHTML = selectedVariant;
        }
      } else {
        productElement.classList.add(
          'step-block__item--hidden'
        );

        productElement.classList.remove(
          'step-block__item--active',
          'final-step__product--active'
        );
      }
    });

    nextStepWrapper
      .closest('.step-block')
      .classList.add('step-block--active');

    if (!this.isFinalBlock && this.layout !== 'quiz') {
      const activeNextBlock =
        this.clickedBlock.closest('.step-block')?.nextElementSibling;

      window.scrollTo({
        top: activeNextBlock.offsetTop - 150,
        behavior: 'smooth',
      });
    }
  }

  setButtonPrice(productItem) {
    const buttonComparePriceElement = this.querySelector(
      '.final-step__product__button-price .price__compare'
    );

    const buttonOriginalPriceElement = this.querySelector(
      '.final-step__product__button-price .price__original'
    );

    const priceWrapper = productItem.querySelector(
      '.price__wrapper'
    );

    const productOriginalPrice =
      priceWrapper?.dataset.priceOriginal ||
      productItem.querySelector('.price__original')?.textContent;

    const productComparePrice =
      priceWrapper?.dataset.priceCompare ||
      productItem.querySelector('.price__compare')?.textContent;

    if (buttonComparePriceElement) {
      buttonComparePriceElement.innerText =
        productComparePrice || '';
    }

    if (buttonOriginalPriceElement) {
      buttonOriginalPriceElement.innerText =
        productOriginalPrice || '';
    }
  }

  showFinalBlock() {
    this.setAttribute('data-current-step', 'final');

    this.isFinalBlock = true;
    this.finalBlock.classList.add('active');

    if (this.layout !== 'quiz') {
      if (!this.isInitialized && this.isOpenedByDefault) {
        this.isInitialized = true;
      } else {
        setTimeout(() => {
          window.scrollTo({
            top:
              window.innerWidth < 768
                ? this.finalBlock.offsetTop - 450
                : this.finalBlock.offsetTop - 600,
            behavior: 'smooth',
          });
        }, 201);
      }
    }
  }

  hideFinalBlock() {
    if (this.isOpenedByDefault) return;

    this.finalBlock.classList.remove('active');
  }

  async addToCart() {
    const object = JSON.parse(
      this.querySelector('#landing-product__object').innerHTML
    ).settings;

    const activeProduct = this.querySelector(
      '.final-step__product--active'
    );

    if (!activeProduct) {
      throw new Error('No active product selected');
    }

    const productId = activeProduct.dataset.finalProductId;

    const selectedVariant =
      this.querySelector(
        '.step-block__item--active[data-variant]'
      )?.dataset.variant || 'Default Title';

    const products = object.products;

    let variantId;
    let currentProduct;

    for (const product of products) {
      if (product.id == productId) {
        currentProduct = product;

        const variant = product.variants.find(
          variant => variant.title === selectedVariant
        );

        variantId = variant?.id;

        break;
      }
    }

    if (!currentProduct) {
      throw new Error(
        `Product ${productId} was not found`
      );
    }

    if (!variantId) {
      throw new Error(
        `Variant "${selectedVariant}" was not found`
      );
    }

    const atcObject = {
      items: [
        {
          id: Number(variantId),
          quantity: Number(activeProduct.dataset.quantity),
        },
      ],
    };

    if (object.subscription_name) {
      const subscriptionGroups =
        currentProduct?.selling_plan_groups?.[0]
          ?.selling_plans || [];

      const sellingPlan = subscriptionGroups.find(
        plan => plan.name === object.subscription_name
      );

      if (sellingPlan) {
        atcObject.items[0].selling_plan = sellingPlan.id;
      }
    }

    if (window.landing_uniq_prop) {
      atcObject.items[0].properties = {
        _landingSubscription: window.landing_uniq_prop,
      };
    }

    const firstVisitedPage = localStorage.getItem(
      'first-visited-page'
    );

    if (firstVisitedPage) {
      atcObject.items[0].properties =
        atcObject.items[0].properties || {};

      atcObject.items[0].properties['_first_visited_page'] =
        firstVisitedPage;
    }

    const addResponse = await fetch(
      `${window.Shopify.routes.root}cart/add.js`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(atcObject),
      }
    );

    if (!addResponse.ok) {
      const errorText = await addResponse.text();

      throw new Error(
        `Add to cart failed: ${addResponse.status} ${errorText}`
      );
    }

    const addedItems = await addResponse.json();

    if (!addedItems) {
      throw new Error(
        'Shopify returned an empty add-to-cart response'
      );
    }

    const cart = await this.getCart();

    if (!cart.item_count) {
      throw new Error(
        'Cart is empty after successful add-to-cart request'
      );
    }

    const addedVariantExists = cart.items?.some(
      item => Number(item.variant_id) === Number(variantId)
    );

    if (!addedVariantExists) {
      throw new Error(
        `Variant ${variantId} is missing from the cart after add`
      );
    }

    let discountCode =
      activeProduct.dataset.quantity > 3 &&
      currentProduct.type !== 'Pod'
        ? object.discount_code_big
        : object.discount_code;

    if (
      object.use_separate_pods_discounts &&
      currentProduct.type === 'Pod'
    ) {
      discountCode =
        activeProduct.dataset.quantity > 2
          ? object.discount_code_pods_big
          : object.discount_code_pods;
    }

    const checkoutUrl = discountCode
      ? `${window.Shopify.routes.root}checkout?discount=${encodeURIComponent(
          discountCode
        )}`
      : `${window.Shopify.routes.root}checkout`;

    window.location.assign(checkoutUrl);
  }
}

customElements.define(
  'landing-product-double',
  LandingProductDoubleSection
);


class LandingProductDoubleSectionNew extends LandingProductDoubleSection {
  constructor() {
    super();

    this.previousStepButton = this.querySelector(
      '.landing-product__previous-step-button'
    );

    this.stepBlocksContainer = this.querySelector(
      '.landing-product__steps-container'
    );

    this.stepProgressWrapper = this.querySelector(
      '.landing-product__steps-progress__wrapper'
    );

    this.progressStepItems = Array.from(
      this.querySelectorAll('[data-progress-step-number]')
    );

    this.previousStepButton?.addEventListener('click', () => {
      this.closeCurrentStep();
    });

    this.progressStepItems.forEach(item => {
      item.addEventListener('click', () => {
        const currentStep = this.dataset.currentStep;

        if (
          +currentStep > +item.dataset.progressStepNumber ||
          currentStep === 'final'
        ) {
          this.setAttribute(
            'data-current-step',
            +item.dataset.progressStepNumber
          );
        }
      });
    });
  }

  highlightBlock() {
    let currentStep = this.clickedBlock.dataset.step;
    let currentStepName = this.clickedBlock.dataset.stepName;
    let prevActiveBlock = this.querySelector(
      `[data-step="${currentStep}"].step-block__item--active`
    );

    if (prevActiveBlock) {
      prevActiveBlock.classList.remove(
        'step-block__item--active'
      );
    }

    this.clickedBlock.classList.add(
      'step-block__item--active'
    );

    if (currentStepName === 'type') {
      this.querySelectorAll(
        '.step-block__item--active'
      ).forEach(activeElement => {
        activeElement.classList.remove(
          'step-block__item--active'
        );

        this.querySelector('[data-step-name="product"]')
          ?.closest('.step-block')
          ?.classList.remove('step-block--active');
      });

      this.isFinalBlock = false;
    }

    if (currentStepName === 'roast') {
      this.isFinalBlock = false;
    }

    if (prevActiveBlock) {
      prevActiveBlock.classList.remove(
        'step-block__item--active'
      );
    }

    this.clickedBlock.classList.add(
      'step-block__item--active'
    );

    if (currentStepName === 'product') {
      let productItem = this.querySelector(
        '[data-step-name="product"].step-block__item--active'
      );

      let prevProduct = this.querySelector(
        '.final-step__product--active'
      );

      if (prevProduct) {
        prevProduct.classList.remove(
          'final-step__product--active'
        );
      }

      productItem.classList.add(
        'final-step__product--active'
      );

      this.setButtonPrice(productItem);
    }

    this.openNextStep();

    if (this.stepProgressWrapper) {
      window.scrollTo({
        top: this.stepProgressWrapper.offsetTop - 62,
        behavior: 'smooth',
      });
    }
  }

  closeCurrentStep() {
    if (
      this.dataset.currentStep === 'final' ||
      this.dataset.currentStep === '4'
    ) {
      this.dataset.currentStep =
        this.clickedBlock.dataset.step;

      this.querySelector(
        '.landing-product__final'
      ).classList.remove('active');

      this.querySelector(
        '[data-step-name="product"].step-block__item--active'
      )?.classList.remove('step-block__item--active');
    } else {
      let currentStepWrapper = this.querySelector(
        `[data-step="${this.dataset.currentStep}"]`
      );

      if (this.dataset.currentStep == 2) {
        this.previousStepButton?.classList.remove('active');
      }

      currentStepWrapper
        ?.closest('.landing-product__step')
        ?.classList.remove(
          'step-block--active',
          'step-block--default-variant-active'
        );

      this.dataset.currentStep = Math.max(
        this.dataset.currentStep - 1,
        1
      );
    }

    if (this.stepProgressWrapper) {
      window.scrollTo({
        top: this.stepProgressWrapper.offsetTop - 62,
        behavior: 'smooth',
      });
    }
  }

  static get observedAttributes() {
    return ['data-current-step'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-current-step') {
      this.handleStepChange(newValue);
    }
  }

  handleStepChange(newStep) {
    let allSteps = this.querySelectorAll(
      '.landing-product__step'
    );

    let finalStep = this.querySelector(
      '.landing-product__final'
    );

    let currentStep = this.querySelector(
      `[data-step="${newStep}"]`
    )?.closest('.landing-product__step');

    if (newStep === '4' || newStep === 'final') {
      allSteps.forEach(step => {
        if (
          !step.contains(
            this.querySelector(
              '[data-step-name="product"]'
            )
          )
        ) {
          step.classList.remove(
            'step-block--active',
            'step-block--default-variant-active'
          );
        }
      });

      finalStep?.classList.add('active');
    } else {
      allSteps.forEach(step => {
        step.classList.remove(
          'step-block--active',
          'step-block--default-variant-active'
        );
      });

      finalStep?.classList.remove('active');
      currentStep?.classList.add('step-block--active');
    }

    this.updateStepsProgress(newStep);
  }

  updateStepsProgress(currentStepIndex) {
    const steps = document.querySelectorAll(
      '.step-progress'
    );

    if (
      currentStepIndex === 'final' ||
      currentStepIndex === '4'
    ) {
      steps.forEach(step => {
        step.classList.add('completed');
      });

      return;
    }

    steps.forEach((step, index) => {
      if (index < currentStepIndex - 1) {
        step.classList.add('completed');
        step.classList.remove('active');
      } else if (
        index === +currentStepIndex - 1
      ) {
        step.classList.add('active');
        step.classList.remove('completed');
      } else {
        step.classList.remove(
          'active',
          'completed'
        );
      }
    });
  }
}

if (!customElements.get('landing-product-double-new')) {
  customElements.define(
    'landing-product-double-new',
    LandingProductDoubleSectionNew
  );
}

class LandingProductRefinedSection extends LandingProductDoubleSection {
  constructor() {
    super();
    this.hoveredRoast = null;
    this.querySelectorAll('[data-step-name="roast"]').forEach(roast => {
      if (this.dataset.roastDescriptionLayout !== 'below') return;
      roast.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
        this.hoveredRoast = roast;
        this.updateRoastDescription();
      });
      roast.addEventListener('pointerleave', () => {
        if (this.hoveredRoast === roast) this.hoveredRoast = null;
        this.updateRoastDescription();
      });
    });
    this.addEventListener('change', event => {
      if (event.target.matches('.landing-product-double__choice')) {
        this.clickedBlock = event.target.closest('[data-step]');
        this.highlightBlock();
      }
    });
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.dataset.currentStep) this.dataset.currentStep = '1';
  }

  setStepVisibility(name, visible) {
    const step = this.querySelector(`[data-alt-step="${name}"]`);
    if (!step) return;
    step.hidden = !visible;
    step.classList.toggle('step-block--active', visible);
    step.querySelector('fieldset').disabled = !visible;
  }

  focusCurrentKeyboardTarget() {
    if (this.dataset.currentStep === 'final') {
      this.addButton.focus();
      return;
    }
    this.querySelector(
      `[data-step="${this.dataset.currentStep}"]:not([hidden]) input:not(:disabled)`
    )?.focus();
  }

  clearChoices(name) {
    this.querySelectorAll(`[data-step-name="${name}"]`).forEach(item => {
      item.classList.remove('step-block__item--active', 'final-step__product--active');
      item.querySelector('input').checked = false;
    });
  }

  setCheckoutVisibility(visible) {
    this.querySelector('.landing-product__final').hidden = !visible;
    this.querySelector('.landing-alt__assurances').hidden = !visible;
    this.finalBlock.classList.toggle('active', visible);
    this.addButton.disabled = !visible;
    this.isFinalBlock = visible;
  }

  updateRoastDescription() {
    if (this.dataset.roastDescriptionLayout !== 'below') return;
    const roast = this.hoveredRoast && !this.hoveredRoast.hidden
      ? this.hoveredRoast
      : this.querySelector('[data-step-name="roast"].step-block__item--active');
    this.querySelector('.landing-alt__roast-description').innerHTML =
      roast?.querySelector('[data-roast-description]')?.innerHTML || '';
  }

  scrollToStep(name) {
    const step = this.querySelector(`[data-alt-step="${name}"]`);
    if (step && !this.isKeyboardStepConfirmation) {
      if (name === 'product' && this.dataset.productStepScroll === 'default') {
        window.scrollTo({ top: step.getBoundingClientRect().top + window.scrollY - 150, behavior: 'smooth' });
        return;
      }
      const context = name === 'product'
        ? this.querySelector('.landing-alt__roast-description')
        : this.querySelector('[data-step-name="type"].step-block__item--active .landing-alt__type-title');

      const headerHeight = this.getStickyHeaderHeight();
      if (this.dataset.productStepScroll === 'heading') {
        const heading = step.querySelector('legend') || step;
        this.scrollToStart(heading);
        return;
      }
      const nextTop = step.getBoundingClientRect().top + window.scrollY - Math.max(150, headerHeight + 24);
      const contextTop = context
        ? context.getBoundingClientRect().top + window.scrollY - headerHeight - 20
        : nextTop;
      window.scrollTo({ top: Math.max(0, Math.min(nextTop, contextTop)), behavior: 'smooth' });
    }
  }

  highlightBlock(scrollToCheckout = true) {
    if (!this.clickedBlock) return;
    const chosen = this.clickedBlock;
    this.selectStepBlock(chosen);

    if (chosen.dataset.stepName === 'type') {
      const family = chosen.dataset.variant === 'Default Title' ? 'pods' : 'bags';
      this.dataset.family = family;
      this.clearChoices('roast');
      this.clearChoices('product');
      this.hoveredRoast = null;
      this.querySelectorAll('[data-step-name="roast"]').forEach(roast => {
        roast.hidden = roast.dataset.family !== family;
        roast.querySelector('input').disabled = roast.hidden;
      });
      this.productsElements.forEach(pack => { pack.hidden = true; });
      const description = this.querySelector('.landing-alt__roast-description');
      if (description) description.innerHTML = '';
      const heading = this.querySelector('[data-bags-heading]');
      heading.textContent = family === 'pods' ? heading.dataset.podsHeading : heading.dataset.bagsHeading;
      this.setStepVisibility('roast', true);
      this.setStepVisibility('product', false);
      this.setCheckoutVisibility(false);
      this.dataset.currentStep = '2';
      this.scrollToStep('roast');
      return;
    }

    if (chosen.dataset.stepName === 'roast') {
      this.clearChoices('product');
      this.updateRoastDescription();
      this.productsElements.forEach(pack => {
        pack.hidden = pack.dataset.finalProductId !== chosen.dataset.product;
        pack.querySelector('input').disabled = pack.hidden;
      });
      this.setStepVisibility('product', true);
      this.setCheckoutVisibility(false);
      this.dataset.currentStep = '3';

      const largePack = Array.from(this.productsElements).find(pack =>
        !pack.hidden && (pack.dataset.quantity === '6' || pack.dataset.quantity === '4')
      );
      if (largePack && !this.isKeyboardStepConfirmation) {
        this.clickedBlock = largePack;
        this.highlightBlock(false);
      }
      this.scrollToStep('product');
      return;
    }

    if (chosen.dataset.stepName === 'product') {
      this.productsElements.forEach(pack =>
        pack.classList.toggle('final-step__product--active', pack === chosen)
      );
      this.setButtonPrice(chosen);
      this.setCheckoutVisibility(true);
      this.dataset.currentStep = 'final';
      if (scrollToCheckout) {
        this.addButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }
}

customElements.define('landing-product-refined', LandingProductRefinedSection);
